import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const wrap = { maxWidth: 820, margin: '0 auto', padding: '40px 20px 60px', color: 'var(--text)' };
const h1 = { fontSize: 28, fontWeight: 800, marginBottom: 4 };
const h2 = { fontSize: 17, fontWeight: 700, margin: '26px 0 8px' };
const p = { fontSize: 14.5, lineHeight: 1.7, color: 'var(--muted)', margin: '0 0 12px' };
const li = { fontSize: 14.5, lineHeight: 1.7, color: 'var(--muted)', marginBottom: 6 };
const meta = { fontSize: 13, color: 'var(--muted)', marginBottom: 20 };

export default function PrivacyPage() {
  return (
    <div style={wrap}>
      <Helmet>
        <title>Privacy Policy — Algovex</title>
        <meta name="description" content="How Algovex collects, uses, and protects your data." />
      </Helmet>
      <h1 style={h1}>Privacy Policy</h1>
      <div style={meta}>Last updated: May 22, 2026</div>

      <p style={p}>
        This Privacy Policy explains how Algovex LLC (“Algovex,” “we,” “us,” or “our”) collects,
        uses, and protects your information when you use our website and services (the “Service”).
        By using the Service, you agree to the practices described here.
      </p>

      <h2 style={h2}>1. Information we collect</h2>
      <ul style={{ paddingLeft: 18, margin: '0 0 12px' }}>
        <li style={li}><strong>Account information</strong> — such as your email address, username, and password (stored in hashed form).</li>
        <li style={li}><strong>Content you create</strong> — the strategies, backtests, and settings you build on the platform.</li>
        <li style={li}><strong>Billing information</strong> — handled by our payment processor (Stripe); we do not store full card details.</li>
        <li style={li}><strong>Usage &amp; device data</strong> — such as pages visited, actions taken, browser type, and analytics events, used to improve the Service.</li>
      </ul>

      <h2 style={h2}>2. How we use your information</h2>
      <ul style={{ paddingLeft: 18, margin: '0 0 12px' }}>
        <li style={li}>To provide, operate, and improve the Service;</li>
        <li style={li}>To process subscriptions and payments;</li>
        <li style={li}>To send transactional and, where you’ve opted in, informational emails;</li>
        <li style={li}>To provide AI-assisted features you request;</li>
        <li style={li}>To secure the Service and prevent abuse; and</li>
        <li style={li}>To comply with legal obligations.</li>
      </ul>

      <h2 style={h2}>3. Service providers</h2>
      <p style={p}>
        We share limited information with third-party providers who help us operate the Service,
        including payment processing (Stripe), AI features (OpenAI), email delivery (Amazon SES),
        market-data sources, and product analytics. These providers process data on our behalf and
        are subject to their own privacy and security commitments.
      </p>

      <h2 style={h2}>4. Cookies &amp; analytics</h2>
      <p style={p}>
        We use cookies and similar technologies to keep you signed in, remember preferences, and
        understand how the Service is used. You can control cookies through your browser settings,
        though some features may not function properly without them.
      </p>

      <h2 style={h2}>5. Data retention</h2>
      <p style={p}>
        We retain your information for as long as your account is active or as needed to provide the
        Service, comply with legal obligations, resolve disputes, and enforce our agreements. You may
        request deletion of your account and associated data as described below.
      </p>

      <h2 style={h2}>6. Security</h2>
      <p style={p}>
        We use reasonable technical and organizational measures to protect your information. However,
        no method of transmission or storage is completely secure, and we cannot guarantee absolute
        security.
      </p>

      <h2 style={h2}>7. Your rights</h2>
      <p style={p}>
        Depending on your location, you may have the right to access, correct, export, or delete your
        personal information, and to object to or restrict certain processing. To exercise these
        rights, contact us using the details below. You can also manage email preferences from your
        profile or via the unsubscribe link in any marketing email.
      </p>

      <h2 style={h2}>8. Children’s privacy</h2>
      <p style={p}>
        The Service is not directed to children under 18, and we do not knowingly collect personal
        information from them.
      </p>

      <h2 style={h2}>9. Changes to this policy</h2>
      <p style={p}>
        We may update this Privacy Policy from time to time. Material changes will be reflected by
        updating the “Last updated” date above and, where appropriate, by additional notice.
      </p>

      <h2 style={h2}>10. Contact</h2>
      <p style={p}>
        Questions about this policy or your data? Reach us via our <Link to="/contact" style={{ color: 'var(--teal)' }}>Contact page</Link>.
      </p>
    </div>
  );
}
